import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import apiClient from '../../utils/api';

const EventsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const EventsList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const EventCard = styled.div<{ status?: string }>`
  padding: 1.5rem;
  border: 2px solid ${props => {
    if (props.status === '진행중') return '#d1e7dd';
    if (props.status === '마감') return '#fff3cd';
    return '#dee2e6';
  }};
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: ${props => {
    if (props.status === '진행중') return '#f8fff9';
    return 'white';
  }};
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const EventTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  flex: 1;
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '진행중') return '#d1e7dd';
    if (props.status === '마감') return '#fff3cd';
    return '#dee2e6';
  }};
  color: ${props => {
    if (props.status === '진행중') return '#0f5132';
    if (props.status === '마감') return '#856404';
    return '#6c757d';
  }};
  white-space: nowrap;
`;

const EventInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 0.75rem;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8125rem;
  color: #6c757d;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.875rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #000000;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const TextAreaLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ViewSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const DetailContent = styled.div`
  font-size: 0.9375rem;
  color: #495057;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1rem;
`;

interface Event {
  id: number;
  title: string;
  description: string;
  eventType: string;
  startDate: string;
  endDate: string;
  openDatetime?: string | null;
  maxParticipants: string;
  currentParticipants: number;
  status: '진행중' | '마감' | '종료';
}

interface EventMemberStatus {
  user_no: number;
  name: string;
  gender: string;
  electionStatus: string;
  role: string;
  applyStatus: '참여' | '미참여';
  participatedAt: string;
  desiredNumber?: string;  // 희망 배번
}

const AdminEvents: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: '',
    startDate: '',
    endDate: '',
    openDatetime: '',
    maxParticipants: '',
    status: '진행중' as '진행중' | '마감' | '종료'
  });

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberStatuses, setMemberStatuses] = useState<EventMemberStatus[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [finishedNumberChangeEvents, setFinishedNumberChangeEvents] = useState<Array<{event_no: number; event_name: string; end_date: string; participant_count: number}>>([]);
  const [linkPreviousEventNo, setLinkPreviousEventNo] = useState<number | null>(null);
  const [includeMembersWithoutNumber, setIncludeMembersWithoutNumber] = useState(false);

  // 이벤트 목록 조회
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/events');
      if (response.data.success) {
        setEvents(response.data.data);
      }
    } catch (error) {
      console.error('이벤트 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // 통계
  const stats = {
    total: events.length,
    ongoing: events.filter(e => e.status === '진행중').length,
    closed: events.filter(e => e.status === '마감').length,
    finished: events.filter(e => e.status === '종료').length,
  };

  const handleAddClick = () => {
    setFormData({
      title: '',
      description: '',
      eventType: '',
      startDate: '',
      endDate: '',
      openDatetime: '',
      maxParticipants: '999',
      status: '진행중'
    });
    setSelectedEvent(null);
    setIsCreateModalOpen(true);
  };

  const handleEventClick = async (event: Event) => {
    setSelectedEvent(event);
    setIsViewModalOpen(true);
    
    // 디버깅: 배번 할당 버튼 표시 조건 확인
    console.log('선택된 이벤트 정보:', {
      id: event.id,
      title: event.title,
      eventType: event.eventType,
      status: event.status,
      '배번할당버튼표시여부': event.eventType === '배번할당' && event.status === '종료'
    });

    // 이벤트별 회원 신청 현황 조회
    try {
      setMemberLoading(true);
      const response = await apiClient.get(`/events/${event.id}/members`);
      if (response.data.success) {
        setMemberStatuses(response.data.data);
        // 디버깅: 배번할당 이벤트인 경우 희망 배번 데이터 확인
        if (event.eventType === '배번할당') {
          console.log('배번할당 이벤트 참여자 데이터:', response.data.data);
        }
      } else {
        setMemberStatuses([]);
      }
    } catch (error) {
      console.error('이벤트 회원 신청 현황 조회 실패:', error);
      setMemberStatuses([]);
    } finally {
      setMemberLoading(false);
    }
  };

  const handleEdit = () => {
    if (!selectedEvent) return;
    
    setFormData({
      title: selectedEvent.title,
      description: selectedEvent.description,
      eventType: selectedEvent.eventType,
      startDate: selectedEvent.startDate,
      endDate: selectedEvent.endDate,
      openDatetime: selectedEvent.openDatetime || '',
      maxParticipants: selectedEvent.maxParticipants,
      status: selectedEvent.status
    });
    setIsViewModalOpen(false);
    setIsCreateModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    if (!formData.description.trim()) {
      alert('설명을 입력해주세요.');
      return;
    }

    try {
      if (selectedEvent) {
        // 이벤트 수정
        const response = await apiClient.put(`/events/${selectedEvent.id}`, {
          event_name: formData.title,
          event_type: formData.eventType,
          description: formData.description,
          start_date: formData.startDate,
          end_date: formData.endDate,
          open_datetime: formData.openDatetime && formData.openDatetime.trim() ? formData.openDatetime.trim() : null,
          max_participants: formData.maxParticipants === '999' ? null : parseInt(formData.maxParticipants),
          status: formData.status
        });
        
        if (response.data.success) {
          window.alert('이벤트가 수정되었습니다');
          fetchEvents();
        }
      } else {
        // 이벤트 추가
        const params: any = {
          event_name: formData.title,
          event_type: formData.eventType,
          description: formData.description,
          start_date: formData.startDate,
          end_date: formData.endDate,
          open_datetime: formData.openDatetime && formData.openDatetime.trim() ? formData.openDatetime.trim() : null,
          max_participants: formData.maxParticipants === '999' ? null : parseInt(formData.maxParticipants),
          status: formData.status
        };
        
        // 배번할당 이벤트이고 대상자 선택 옵션이 있는 경우 query parameter로 전달
        const queryParams: any = {};
        if (formData.eventType === '배번할당') {
          if (linkPreviousEventNo) {
            queryParams.link_previous_event_no = linkPreviousEventNo;
          }
          if (includeMembersWithoutNumber) {
            queryParams.include_members_without_number = true;
          }
        }
        
        const response = await apiClient.post('/events', params, {
          params: queryParams
        });
        
        if (response.data.success) {
          window.alert('이벤트가 등록되었습니다');
          fetchEvents();
          setIsCreateModalOpen(false);
          setSelectedEvent(null);
          setLinkPreviousEventNo(null);
          setIncludeMembersWithoutNumber(false);
        }
      }

      setIsCreateModalOpen(false);
      setSelectedEvent(null);
    } catch (error: any) {
      console.error('이벤트 저장 실패:', error);
      window.alert(error.response?.data?.detail || '이벤트 저장에 실패했습니다');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    if (window.confirm('이 이벤트를 삭제하시겠습니까?')) {
      try {
        const response = await apiClient.delete(`/events/${selectedEvent.id}`);
        if (response.data.success) {
          window.alert('이벤트가 삭제되었습니다');
          fetchEvents();
          setIsViewModalOpen(false);
          setSelectedEvent(null);
        }
      } catch (error: any) {
        console.error('이벤트 삭제 실패:', error);
        window.alert(error.response?.data?.detail || '이벤트 삭제에 실패했습니다');
      }
    }
  };

  const handleAssignNumbers = async () => {
    if (!selectedEvent) return;
    
    if (window.confirm('선착순으로 배번을 할당하시겠습니까?\n\n대상:\n- 이벤트 신청자들 (참여 시간순)\n- 현재 배번이 없는 회원들')) {
      try {
        const response = await apiClient.post(`/events/${selectedEvent.id}/assign-numbers`);
        if (response.data.success) {
          const result = response.data.data;
          window.alert(
            `배번 할당이 완료되었습니다.\n\n` +
            `총 대상자: ${result.total_candidates}명\n` +
            `할당 성공: ${result.assigned_count}명\n` +
            `할당 실패: ${result.failed_count}명`
          );
          setIsViewModalOpen(false);
          setSelectedEvent(null);
          fetchEvents();
        }
      } catch (error: any) {
        console.error('배번 할당 실패:', error);
        window.alert(error.response?.data?.detail || '배번 할당에 실패했습니다');
      }
    }
  };

  return (
    <DashboardLayout title="이벤트 관리">
      <EventsContainer>
        <Header>
          <Title>이벤트 관리</Title>
          <Button variant="primary" onClick={handleAddClick}>
            + 이벤트 추가
          </Button>
        </Header>
        
        <StatsRow>
          <StatCard>
            <StatLabel>전체</StatLabel>
            <StatValue>{stats.total}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>진행중</StatLabel>
            <StatValue>{stats.ongoing}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>마감</StatLabel>
            <StatValue>{stats.closed}건</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>종료</StatLabel>
            <StatValue>{stats.finished}건</StatValue>
          </StatCard>
        </StatsRow>
        
        <EventsList>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : events.length > 0 ? (
            events.map(event => (
              <EventCard key={event.id} status={event.status} onClick={() => handleEventClick(event)}>
                <EventHeader>
                  <EventTitle>🎉 {event.title}</EventTitle>
                  <StatusBadge status={event.status}>{event.status}</StatusBadge>
                </EventHeader>
                <EventInfo>{event.description}</EventInfo>
                <EventMeta>
                  <span>📅 {event.startDate} ~ {event.endDate}</span>
                  <span>•</span>
                  <span>👥 {event.currentParticipants}/{event.maxParticipants}명</span>
                  <span>•</span>
                  <span>🏷️ {event.eventType}</span>
                </EventMeta>
              </EventCard>
            ))
          ) : (
            <EmptyState>등록된 이벤트가 없습니다.</EmptyState>
          )}
        </EventsList>
        
        {/* 이벤트 작성/수정 모달 */}
        <ModalOverlay 
          isOpen={isCreateModalOpen}
          onClick={() => setIsCreateModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedEvent ? '이벤트 수정' : '이벤트 추가'}</ModalTitle>
              <CloseButton onClick={() => setIsCreateModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalForm onSubmit={handleSubmit}>
              <Input
                label="이벤트 제목"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="예: 유니폼 배번 변경 이벤트"
                required
              />
              
              <div>
                <TextAreaLabel>
                  이벤트 설명 <span style={{ color: '#dc3545' }}>*</span>
                </TextAreaLabel>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="이벤트 내용을 자세히 입력하세요"
                  required
                />
              </div>
              
              <Input
                label="이벤트 타입"
                type="text"
                value={formData.eventType}
                onChange={(e) => {
                  setFormData({ ...formData, eventType: e.target.value });
                  // 배번할당이 아니면 대상자 선택 옵션 초기화
                  if (e.target.value !== '배번할당') {
                    setLinkPreviousEventNo(null);
                    setIncludeMembersWithoutNumber(false);
                  }
                }}
                placeholder="예: 배번변경, 배번할당, 쿠폰, 기타"
                required
              />
              
              {/* 배번할당 이벤트일 때만 대상자 선택 옵션 표시 */}
              {formData.eventType === '배번할당' && !selectedEvent && (
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <div style={{ marginBottom: '1rem', fontWeight: 600, fontSize: '0.875rem', color: '#343a40' }}>
                    이벤트 대상자 선택
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={includeMembersWithoutNumber}
                        onChange={(e) => setIncludeMembersWithoutNumber(e.target.checked)}
                      />
                      현재 배번이 없는 회원 포함
                    </label>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                      이전 배번변경 이벤트 연계
                    </label>
                    <select
                      value={linkPreviousEventNo || ''}
                      onChange={(e) => setLinkPreviousEventNo(e.target.value ? parseInt(e.target.value) : null)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '2px solid #dee2e6',
                        borderRadius: '8px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="">선택 안 함</option>
                      {finishedNumberChangeEvents.map(event => (
                        <option key={event.event_no} value={event.event_no}>
                          {event.event_name} ({event.end_date}) - {event.participant_count}명
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              <Input
                label="시작일"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
              
              <Input
                label="종료일"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
              
              <Input
                label="오픈 시간 (선택사항)"
                type="datetime-local"
                value={formData.openDatetime}
                onChange={(e) => setFormData({ ...formData, openDatetime: e.target.value })}
                placeholder="이벤트 오픈 시간을 설정하세요"
              />
              
              <Input
                label="최대 참가 인원"
                type="text"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                placeholder="선착순이 아니면 999 입력"
                required
              />
              
              <RadioGroup>
                <RadioLabel>상태</RadioLabel>
                <RadioOptions>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="status"
                      value="진행중"
                      checked={formData.status === '진행중'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    />
                    진행중
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="status"
                      value="마감"
                      checked={formData.status === '마감'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    />
                    마감
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="status"
                      value="종료"
                      checked={formData.status === '종료'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    />
                    종료
                  </RadioOption>
                </RadioOptions>
              </RadioGroup>
              
              <ModalActions>
                <Button
                  variant="outline"
                  size="large"
                  fullWidth
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  {selectedEvent ? '수정' : '등록'}
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
        
        {/* 이벤트 상세 보기 모달 */}
        <ModalOverlay 
          isOpen={isViewModalOpen}
          onClick={() => setIsViewModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>이벤트 상세</ModalTitle>
              <CloseButton onClick={() => setIsViewModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {selectedEvent && (
              <>
                <ViewSection>
                  <DetailRow>
                    <DetailLabel>제목</DetailLabel>
                    <DetailValue>{selectedEvent.title}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>이벤트 타입</DetailLabel>
                    <DetailValue>{selectedEvent.eventType}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>기간</DetailLabel>
                    <DetailValue>{selectedEvent.startDate} ~ {selectedEvent.endDate}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>참가 인원</DetailLabel>
                    <DetailValue>{selectedEvent.currentParticipants}/{selectedEvent.maxParticipants}명</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>상태</DetailLabel>
                    <DetailValue>
                      <StatusBadge status={selectedEvent.status}>
                        {selectedEvent.status}
                      </StatusBadge>
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>설명</DetailLabel>
                    <DetailContent>{selectedEvent.description}</DetailContent>
                  </DetailRow>
                </ViewSection>

                <ViewSection>
                  <DetailRow>
                    <DetailLabel>회원 신청 현황</DetailLabel>
                    <DetailValue>
                      {memberLoading ? (
                        <span>로딩 중...</span>
                      ) : (
                        <>
                          <div style={{ marginBottom: '0.5rem' }}>
                            전체 {memberStatuses.length}명 중{' '}
                            <span style={{ fontWeight: 700 }}>
                              {memberStatuses.filter(m => m.applyStatus === '참여').length}명 신청
                            </span>
                          </div>
                          <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
                            {memberStatuses.map(member => (
                              <div 
                                key={member.user_no} 
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '0.35rem 0', 
                                  borderBottom: '1px solid #f1f3f5',
                                  fontSize: '0.8125rem'
                                }}
                              >
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 600 }}>{member.name}</span>
                                  <span style={{ color: '#868e96' }}>
                                    {member.gender} / {member.electionStatus} / {member.role}
                                  </span>
                                  {member.applyStatus === '참여' && 
                                   selectedEvent?.eventType === '배번할당' && 
                                   member.desiredNumber && 
                                   member.desiredNumber.toString().trim() !== '' && (
                                    <span style={{ 
                                      color: '#198754', 
                                      fontWeight: 600,
                                      backgroundColor: '#d1e7dd',
                                      padding: '0.125rem 0.5rem',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      희망: {member.desiredNumber}번
                                    </span>
                                  )}
                                </div>
                                <div>
                                  {member.applyStatus === '참여' ? (
                                    <span style={{ color: '#0f5132', fontWeight: 700 }}>참여</span>
                                  ) : (
                                    <span style={{ color: '#adb5bd' }}>미참여</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </DetailValue>
                  </DetailRow>
                </ViewSection>
                
                <ModalActions>
                  {selectedEvent && selectedEvent.eventType === '배번할당' && selectedEvent.status === '종료' && (
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleAssignNumbers}
                      style={{ backgroundColor: '#198754', borderColor: '#198754' }}
                    >
                      배번 할당
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleEdit}
                  >
                    수정
                  </Button>
                  <Button
                    variant="danger"
                    size="large"
                    onClick={handleDelete}
                  >
                    삭제
                  </Button>
                </ModalActions>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      </EventsContainer>
    </DashboardLayout>
  );
};

export default AdminEvents;

